import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, useToast } from "@platform/ui";
import type { SettingValues } from "../api/client";
import { useApi } from "../context";
import { useT } from "../i18n";
import { isVersionConflict } from "./etag";
import { errorText } from "./errors";

/**
 * Load a setting with its ETag, keep an editable draft, save with If-Match.
 * A 412 flags `conflict` so the page can offer "reload" instead of silently overwriting.
 */
export function useSetting<K extends keyof SettingValues>(key: K) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const query = useQuery({ queryKey: ["settings", key], queryFn: () => api.settings.get(key) });
  const [draft, setDraft] = useState<SettingValues[K] | undefined>(undefined);
  const [conflict, setConflict] = useState(false);
  useEffect(() => {
    if (query.data) setDraft(structuredClone(query.data.value));
  }, [query.data]);
  const save = useMutation({
    mutationFn: (value: SettingValues[K]) => {
      if (!query.data) throw new Error("not loaded");
      return api.settings.put(key, value, query.data.etag);
    },
    onSuccess: (env) => {
      qc.setQueryData(["settings", key], env);
      setConflict(false);
      toast({ title: t("common.saved"), tone: "success" });
    },
    onError: (e) => {
      if (isVersionConflict(e)) setConflict(true);
    },
  });
  const reload = async () => {
    setConflict(false);
    save.reset();
    await query.refetch();
  };
  return { query, draft, setDraft, save, conflict, reload };
}

export function SettingErrors({
  state,
  conflictLabel,
}: {
  state: { conflict: boolean; save: { isError: boolean; error: unknown }; reload: () => Promise<void> };
  conflictLabel: string;
}) {
  const { t } = useT();
  if (state.conflict) {
    return (
      <div data-testid="settings-conflict" className="px-6 pt-4">
        <Alert
          tone="warning"
          action={
            <Button size="sm" variant="secondary" onClick={() => void state.reload()}>
              {t("common.reload")}
            </Button>
          }
        >
          {conflictLabel}
        </Alert>
      </div>
    );
  }
  if (state.save.isError) {
    return (
      <div className="px-6 pt-4">
        <Alert tone="error">{errorText(state.save.error)}</Alert>
      </div>
    );
  }
  return null;
}
